import React, { useEffect, Suspense, lazy } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { HelmetProvider } from 'react-helmet-async';
import Header from "./components/Header";
import Footer from "./components/Footer";
import CookieConsent from "./components/CookieConsent";
import Home from "./pages/Home";
import { Toaster } from "./components/ui/sonner";
import { getLanguageFromPath, SUPPORTED_LANGUAGES } from "./utils/languageRouting";
import { ensureLanguage } from "./i18n";
import { locationRoutes } from "./data/locationRoutes";

// Lazy-load all routes EXCEPT Home (Home is the LCP-critical page; loading it
// in the main bundle avoids a chunk fetch round-trip on first paint).
const About = lazy(() => import("./pages/About"));
const Services = lazy(() => import("./pages/Services"));
const WhatIsStoneCarpet = lazy(() => import("./pages/WhatIsStoneCarpet"));
const Gallery = lazy(() => import("./pages/Gallery"));
const FAQ = lazy(() => import("./pages/FAQ"));
const Contact = lazy(() => import("./pages/Contact"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const LocationLanding = lazy(() => import("./pages/LocationLanding"));

// Minimal fallback while a route chunk is fetching. No layout shift: Header is
// already mounted, this just fills the page area briefly.
const RouteFallback = () => (
  <div style={{ minHeight: "60vh" }} aria-hidden="true" />
);

// Sync URL language with i18n. Loads the dynamic translation chunk BEFORE
// calling changeLanguage so the next render is immediately in the target
// language (no 2-click delay).
function LanguageSync() {
  const location = useLocation();
  const { i18n } = useTranslation();

  useEffect(() => {
    const urlLang = getLanguageFromPath(location.pathname);
    if (i18n.language !== urlLang) {
      ensureLanguage(urlLang);
    }
  }, [location.pathname, i18n]);

  return null;
}

function App() {
  return (
    <HelmetProvider>
      <div className="App">
        <BrowserRouter>
          <LanguageSync />
          <Header />
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              {/* English routes (no prefix) */}
              <Route path="/" element={<Home />} />
              <Route path="/about" element={<About />} />
              <Route path="/services" element={<Services />} />
              <Route path="/what-is-stone-carpet" element={<WhatIsStoneCarpet />} />
              <Route path="/gallery" element={<Gallery />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              {locationRoutes.map((route) => (
                <Route
                  key={route.slug}
                  path={route.path}
                  element={<LocationLanding slug={route.slug} />}
                />
              ))}

              {/* Language-prefixed routes for ES, PL, SV */}
              {SUPPORTED_LANGUAGES.map(lang => (
                <React.Fragment key={lang}>
                  <Route path={`/${lang}`} element={<Home />} />
                  <Route path={`/${lang}/about`} element={<About />} />
                  <Route path={`/${lang}/services`} element={<Services />} />
                  <Route path={`/${lang}/what-is-stone-carpet`} element={<WhatIsStoneCarpet />} />
                  <Route path={`/${lang}/gallery`} element={<Gallery />} />
                  <Route path={`/${lang}/faq`} element={<FAQ />} />
                  <Route path={`/${lang}/contact`} element={<Contact />} />
                  <Route path={`/${lang}/privacy-policy`} element={<PrivacyPolicy />} />
                  {locationRoutes.map((route) => (
                    <Route
                      key={`${lang}-${route.slug}`}
                      path={`/${lang}${route.path}`}
                      element={<LocationLanding slug={route.slug} />}
                    />
                  ))}
                </React.Fragment>
              ))}
            </Routes>
          </Suspense>
          <Footer />
          <CookieConsent />
          <Toaster />
        </BrowserRouter>
      </div>
    </HelmetProvider>
  );
}

export default App;
